import { defineStore } from 'pinia';
import { ref } from 'vue';
import { staticRoutes } from '@/router/staticRoutes';
import type { RouteRecordRaw } from 'vue-router';
import { generateRoutesFromMenus, filterMenuByNames, extractButtonAliases } from '@/router/routeHelper';
import type { MenuItem } from '@/types/api/menu.type';
import { appApi } from '@/plugins/api';

const usePermissionStore = defineStore('permission', () => {
  const routes = ref<RouteRecordRaw[]>([]);
  const addRoutes = ref<RouteRecordRaw[]>([]);
  const buttonList = ref<string[]>([]);
  const sideMenus = ref<MenuItem[]>([]); // 存储原始菜单数据（用于侧边栏渲染）
  const isRoutesLoaded = ref(false);

  /**
 * 从 接口获取菜单数据
 */
  async function loadMenuFromAPI(): Promise<{
    menuList: MenuItem[],
    buttonList: string[],
  }> {
    try {
      const response = await appApi.Menu.getCurrentRoleMenu();

      const menus = response.data.data.menus || [];

      // 从菜单数据中提取所有按钮权限的 alias
      const buttonList_ = extractButtonAliases(menus);

      return {
        menuList: menus,
        buttonList: buttonList_
      };
    } catch (error) {
      console.error('[菜单加载] 加载菜单数据失败:', error);

      return {
        menuList: [],
        buttonList: []
      };
    }
  }

  // 生成路由
  const generateRoutes = async (_roles: string[]) => {
    try {
      const { menuList, buttonList: buttons } = await loadMenuFromAPI();

      console.log(menuList);
      // 开发时进行过滤 只展示开发过的内容{ name: 'Record Query', children: [{ name: 'Sports Tickets' }] }
      const filteredMenus = filterMenuByNames(menuList, [{ name: 'User Management', children: [{ name: 'User List' }, { name: 'S-E Review' }, { name: 'Blacklist' }] }]);
      // const filteredMenus = menuList; // 生产环境拿全量

      // 存储原始菜单数据
      sideMenus.value = filteredMenus;

      // 将菜单数据转换为路由配置
      const accessedRoutes = generateRoutesFromMenus(filteredMenus);

      // 更新路由数据
      routes.value = staticRoutes.concat(accessedRoutes);
      addRoutes.value = accessedRoutes;
      isRoutesLoaded.value = true;

      // 设置按钮权限列表（alias 列表）
      buttonList.value = buttons;

      return accessedRoutes;
    } catch (error) {
      console.error('[权限Store] 生成路由失败:', error);

      return [];
    }
  };

  const resetRoutes = () => {
    isRoutesLoaded.value = false;
    routes.value = [];
    addRoutes.value = [];
    buttonList.value = [];
    sideMenus.value = [];
  };

  // 设置按钮权限列表
  const setButtonList = (buttons: string[]) => {
    buttonList.value = buttons;
  };

  // 检查是否有按钮权限（通过 alias 检查）
  const hasButtonPermission = (alias: string) => {
    return buttonList.value.includes(alias);
  };

  return {
    routes,
    addRoutes,
    buttonList,
    sideMenus,
    isRoutesLoaded,
    generateRoutes,
    setButtonList,
    hasButtonPermission,
    resetRoutes
  };
});

export default usePermissionStore;